import { themeVar as colors, type as typeTokens } from "../design-tokens";
import { Band, blockStyle } from "../ui/Block";

/**
 * MARKET CONDITIONS (DATE OF SALE) — the agent's override for the grid's time adjustment.
 * (2026-08-10, owner: "current market conditions compared to the comp sale date market conditions.")
 *
 * WHAT THE SERVER DOES WITH THIS. It travels on `meta.market_conditions` and is read by
 * `server/amb_build.py`, which hands it to `engine.amb.resolve_market_trend` as
 * `params["market_trend_override"]`. That function is a four-rung ladder — the agent's figure, then
 * a rate a data source actually states, then one derived from the file's own closed sales, then
 * nothing — and this control is the TOP rung: whatever is entered here wins over anything measured,
 * and the report says on the page that the figure is the agent's rather than a measurement.
 *
 * LEAVING IT BLANK IS THE NORMAL CASE and it is not a gap. Blank means "use the sales in my file",
 * and if those sales cannot establish a trend (fewer than four dated arm's-length closings, a span
 * under six months, or a fit the dates do not explain) the report applies NO time adjustment at all
 * and says why. It never falls back to an assumed rate. That is the whole reason this box exists:
 * the alternative to an agent's real figure is nothing, never a guess.
 *
 * A MAGNITUDE AND A DIRECTION, NEVER A BARE SIGNED NUMBER, and that shape is not decoration. On
 * 2026-08-10 the owner reported a lot/view entry whose typed minus sign was silently discarded and
 * the sign re-derived from a dropdown, producing a swing in the wrong direction on the headline.
 * This line lands on EVERY comparable at once, so the same mistake here would be several times
 * larger. The direction owns the sign, a negative magnitude with "rising" is a contradiction the
 * agent has to resolve rather than something we resolve for them (the engine halts on it too), and
 * the preview below states the consequence in the direction it will actually be applied.
 *
 * WHY "RISING" ADDS TO AN OLDER SALE. A sale that closed six months ago closed into the market of
 * six months ago. If prices have risen since, that sale price understates what the same house would
 * fetch today, so the comparable is adjusted UP toward today's market. Falling market, same sale,
 * adjusted DOWN. The wording of the two options says this out loud, because it is the one line on
 * the grid whose direction reads backwards to most people the first time they meet it.
 */
export type MarketConditions = {
  /** Magnitude only, in percent per month. The direction below carries the sign. */
  rate_pct_per_month?: number | null;
  direction?: "rising" | "falling" | null;
  /** Optional: where the agent's figure came from, printed after the basis sentence. */
  note?: string | null;
};

export function MarketConditionsPanel({
  value,
  onChange,
}: {
  value: MarketConditions;
  onChange: (next: MarketConditions) => void;
}) {
  function set(patch: Partial<MarketConditions>) {
    onChange({ ...value, ...patch });
  }

  const magnitude = num(value.rate_pct_per_month);
  const dir = value.direction ?? null;
  const contradiction = magnitude != null && magnitude < 0 && dir === "rising";
  const preview = describeMarketConditions(value);

  return (
    <section style={{ marginBottom: 26, fontFamily: typeTokens.fontUi, color: colors.textPrimary }}>
      <h3
        style={{
          fontSize: 13,
          textTransform: "uppercase",
          letterSpacing: "0.04em",
          color: colors.textSecondary,
          marginBottom: 6,
        }}
      >
        Market conditions since each sale closed
      </h3>
      <Band>
      <div>
      <p
        style={{
          fontSize: 13,
          color: colors.textSecondary,
          marginTop: 0,
          marginBottom: 12,
          lineHeight: 1.5,
          ...blockStyle("copy"),
        }}
      >
        A sale that closed months ago closed into a different market than the one your seller is
        pricing into today. Leave this blank and we measure the movement from the closed sales in
        your own file. If those sales can&apos;t establish a trend, no time adjustment is applied at
        all and the report says why &mdash; we never assume a rate. Enter your MLS market statistics
        here and your figure is used instead, on its own line, named as yours.
      </p>
      </div>

      {/* THE EXPLANATION AND THE CONTROLS ARE TWO COLUMNS (2026-09-01, W3-UIFIX2). The paragraph
          above is capped at `measure.copy` and the boxes below it were too, so the whole section
          rendered as one 640px column with the rest of the page empty beside it. They are not the
          same kind of content: one is prose that has a right measure, the other is a field row that
          has a right width. `Band` pairs them on its own container and stacks them below 904px, so
          a phone and a `prose` page get exactly what they had. */}
      <div>
      <div
        style={{
          display: "grid",
          gridTemplateColumns: "1.4fr 1fr",
          alignItems: "start",
          gap: 10,
          padding: "10px 12px",
          background: colors.bgSurface,
          border: `1px solid ${colors.border}`,
          borderRadius: 8,
          ...blockStyle("copy"),
        }}
      >
        <label style={{ fontSize: 13 }}>
          Monthly change in this submarket
          <div style={{ fontSize: 11, color: colors.textSecondary, marginTop: 2, lineHeight: 1.4 }}>
            Per month, not per year. The direction sets the sign &mdash; a negative number
            can&apos;t flip it.
          </div>
        </label>
        <div style={{ display: "flex", gap: 6, alignItems: "center", flexWrap: "wrap" }}>
          <input
            type="number"
            step="0.01"
            aria-label="Monthly market change"
            value={value.rate_pct_per_month ?? ""}
            placeholder="—"
            // UI-064, same reason as every other number box in this flow: a number input eats the
            // wheel gesture and silently changes the value with it.
            onWheel={(e) => e.currentTarget.blur()}
            onChange={(e) =>
              set({ rate_pct_per_month: e.target.value === "" ? null : Number(e.target.value) })
            }
            style={{
              width: 90,
              padding: "5px 8px",
              borderRadius: 6,
              border: `1px solid ${colors.border}`,
              fontFamily: "monospace",
              fontSize: 13,
            }}
          />
          <span style={{ fontSize: 13 }}>% / month</span>
          <select
            aria-label="Market direction"
            value={value.direction ?? ""}
            onChange={(e) =>
              set({ direction: (e.target.value || null) as MarketConditions["direction"] })
            }
            style={{
              padding: "5px 8px",
              borderRadius: 6,
              border: `1px solid ${colors.border}`,
              fontSize: 13,
            }}
          >
            <option value="">Choose one</option>
            <option value="rising">Rising — earlier sales adjust up</option>
            <option value="falling">Falling — earlier sales adjust down</option>
          </select>
        </div>
      </div>

      <input
        type="text"
        aria-label="Where this figure came from"
        value={value.note ?? ""}
        maxLength={160}
        placeholder="Where this came from (e.g. NTREIS monthly market statistics, July 2026)"
        onChange={(e) => set({ note: e.target.value === "" ? null : e.target.value })}
        style={{
          marginTop: 8,
          width: "100%",
          ...blockStyle("copy"),
          padding: "8px 10px",
          borderRadius: 8,
          border: `1px solid ${colors.border}`,
          fontFamily: typeTokens.fontUi,
          fontSize: 13,
          boxSizing: "border-box",
        }}
      />

      {/* The consequence, in the direction it will actually be applied, before Generate rather than
          after. An adjustment that lands on every comparable at once is the last one an agent
          should be discovering in a PDF they have already sent. */}
      {preview && (
        <div
          role="status"
          style={{
            marginTop: 12,
            padding: "12px 14px",
            borderRadius: 10,
            background: colors.accentSoft,
            border: `1px solid ${colors.border}`,
            ...blockStyle("copy"),
          }}
        >
          <p style={{ margin: 0, fontSize: 13 }}>{preview}</p>
        </div>
      )}

      {/* An amount with no direction is silently discarded by the engine (same rule as lot / view),
          so it is called out here instead of being discovered in the output. */}
      {magnitude != null && magnitude !== 0 && !dir && (
        <p role="alert" style={{ marginTop: 10, fontSize: 12, color: colors.caution, ...blockStyle("copy") }}>
          Choose rising or falling, or this rate won&apos;t be applied &mdash; we won&apos;t assume
          which way the market moved.
        </p>
      )}

      {contradiction && (
        <p role="alert" style={{ marginTop: 10, fontSize: 12, color: colors.caution, ...blockStyle("copy") }}>
          That rate is negative but the direction says rising, and those two say opposite things.
          Enter {Math.abs(magnitude!)}% for a rising market, or switch the direction to falling.
        </p>
      )}
      </div>
      </Band>
    </section>
  );
}

/** What the report will say, phrased the way the report phrases it. Deliberately mirrors
 * `engine.amb._agent_market_trend`: the direction owns the sign, a magnitude with no direction
 * contributes nothing, and a contradiction is refused rather than resolved. Nothing here predicts
 * a DOLLAR figure — the per-comparable amount depends on each sale's close date and price, which
 * this screen does not have, and inventing a preview number is how the pool preview went wrong. */
export function describeMarketConditions(v: MarketConditions): string {
  const mag = num(v.rate_pct_per_month);
  if (mag == null || mag === 0) return "";
  if (v.direction !== "rising" && v.direction !== "falling") return "";
  if (mag < 0 && v.direction === "rising") return "";
  const pct = Math.abs(mag);
  return v.direction === "rising"
    ? `Each comparable will be adjusted up by ${pct}% of its sale price for every month between its close date and today, shown on the grid as a market-conditions line and named as your figure.`
    : `Each comparable will be adjusted down by ${pct}% of its sale price for every month between its close date and today, shown on the grid as a market-conditions line and named as your figure.`;
}

function num(v: number | null | undefined): number | null {
  if (v === null || v === undefined) return null;
  const n = Number(v);
  return Number.isFinite(n) ? n : null;
}
